import React from "react";
import { crewSearchDisabled, crewSearchReason } from "@/lib/crewAccess";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { TID } from "@/lib/testIds";
import { NotifBell } from "@/pages/Notifications";
import { ContextHelp, DashboardHelp } from "@/help/ContextHelp";

export function Layout({ children, draftWorkforceKind }) {
  const { user, signout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const isAdmin = user?.roles?.includes("super_admin");
  const roles = user?.roles || [];
  const contractorKinds = [roles.includes("lead_contractor") && "lead", roles.includes("helper") && "helper"].filter(Boolean);
  const accountRole = [
    isAdmin && "Administrator",
    roles.includes("client") && "Client",
    (roles.includes("contractor") || contractorKinds.length > 0) &&
      `${user?.workforce_kind === "independent" ? "Independent AV technician" : "Contractor"}${contractorKinds.length ? ` (${contractorKinds.join(", ")})` : ""}`,
  ].filter(Boolean).join(" / ") || "Member";
  const accountName = user?.display_name?.trim() || user?.email || "Your account";
  const linkCls = (path) =>
    `px-3 py-1.5 rounded-full text-sm font-medium transition ${
      loc.pathname === path
        ? "bg-[var(--avcp-primary-soft)] text-[var(--avcp-primary)]"
        : "text-[var(--avcp-ink-soft)] hover:text-[var(--avcp-ink)]"
    }`;

  return (
    <div className="min-h-screen">
      <header className="border-b border-[var(--avcp-line)] bg-[var(--avcp-surface)]/80 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex flex-wrap gap-x-4 gap-y-3 items-center justify-between">
          <Link to={user ? "/app" : "/"} data-testid={TID.navHome} className="flex items-center gap-2">
            <span
              aria-hidden
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{
                background:
                  "linear-gradient(135deg, var(--avcp-primary) 0%, #155e75 100%)",
              }}
            >
              <span className="text-white font-bold text-sm tracking-tight">WP</span>
            </span>
            <span className="font-semibold tracking-tight">
              Workforce Partners
            </span>
          </Link>
          {user && (
              <div className="flex items-center gap-3 ml-auto max-w-full">
                <div data-testid="nav-signed-in-as" className="min-w-0 text-right text-xs leading-5 text-[var(--avcp-ink-soft)]" style={{ maxWidth: "20rem", overflowWrap: "anywhere" }}>
                  <span className="block">Signed in as <strong className="font-semibold">{accountRole}</strong></span>
                  <span className="block text-sm font-medium text-[var(--avcp-ink)]">{accountName}</span>
                </div>
                <button
                  data-testid={TID.navLogout}
                  onClick={async () => { await signout(); nav("/signin"); }}
                  className="avcp-btn ghost shrink-0"
                >
                  Sign out
                </button>
              </div>
          )}
          {user ? (
            <nav aria-label="Main navigation" className="flex flex-wrap items-center gap-1 w-full border-t border-[var(--avcp-line)] pt-2">
              <Link to="/app" className={linkCls("/app")} data-testid={TID.navHome}>Home</Link>
              {(roles.includes("client") || isAdmin) && <Link to="/app/jobs" className={linkCls("/app/jobs")} data-testid="nav-jobs">Projects</Link>}
              {(roles.includes("client") || isAdmin) &&
                <Link to="/app/find-contractors" className={linkCls("/app/find-contractors")} data-testid="nav-find-contractors">Find Contractors</Link>}
              {roles.some(r => ["contractor","lead_contractor","helper","super_admin"].includes(r)) && (crewSearchDisabled(user, draftWorkforceKind) ? <button type="button" disabled aria-disabled="true" title={crewSearchReason} className="px-3 py-1.5 rounded-full text-sm font-medium text-slate-400 cursor-not-allowed" data-testid="nav-find-crew">Find Crew</button> : <Link to="/app/find-crew" className={linkCls("/app/find-crew")} data-testid="nav-find-crew">Find Crew</Link>)}
              {(user.roles?.includes("contractor") || user.roles?.includes("lead_contractor") || user.roles?.includes("helper")) && (
                <>
                  <Link to="/app/contractor-profile" className={linkCls("/app/contractor-profile")} data-testid={TID.navProfile}>Profile</Link>
                  <Link to="/app/contractor-profile/availability" className={linkCls("/app/contractor-profile/availability")} data-testid="nav-availability">Availability</Link>
                </>
              )}
              {roles.some(r => ['contractor','lead_contractor','helper','super_admin'].includes(r)) && <Link to="/app/leads/me/bookings" className={linkCls("/app/leads/me/bookings")} data-testid="nav-lead-inbox">Job Requests</Link>}
              {(user.roles?.includes("helper") || user.roles?.includes("lead_contractor") || user.roles?.includes("super_admin")) && (
                <Link to="/app/helpers/me/invitations" className={linkCls("/app/helpers/me/invitations")} data-testid="nav-helper-invitations">Invitations</Link>
              )}
              <Link to="/app/account" className={linkCls("/app/account")} data-testid={TID.navAccount}>Account</Link>
              <Link to="/app/security" className={linkCls("/app/security")} data-testid={TID.navSecurity}>Security</Link>
              {isAdmin && (
                <Link to="/app/admin" className={linkCls("/app/admin")} data-testid={TID.navAdmin}>Admin</Link>
              )}
              <Link to="/help" className={linkCls("/help")} data-testid="nav-help">Help</Link>
              <NotifBell />

            </nav>
          ) : (
            <nav aria-label="Main navigation" className="flex flex-wrap items-center gap-2">
              <Link to="/help" className={linkCls("/help")}>Help</Link>
              <Link to="/signin" className="avcp-btn ghost">Sign in</Link>
              <Link to="/signup" className="avcp-btn primary">Get started</Link>
            </nav>
          )}
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-6 py-8">
        {loc.pathname === "/app" && user && <DashboardHelp />}
        {children}
      </main>
      <ContextHelp />
      <footer className="max-w-6xl mx-auto px-6 py-8 text-xs text-[var(--avcp-ink-soft)]">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span>Workforce Partners · The Professional AV Workforce Network.</span>
          <div className="flex gap-4"><Link to="/help" className="avcp-link">Help center</Link><Link to="/help/contact" className="avcp-link">Contact support</Link></div>
        </div>
      </footer>
    </div>
  );
}
